/**
 * SecurityBrief — Deep South conflict summary for the SEC lens.
 *
 * Shows:
 *  • A martial-law / emergency-decree STATUS BANNER (from /api/security/status)
 *  • 30-day incident count + fatalities (from /api/security/incidents)
 *  • Breakdown of the dominant event types
 *  • The top SEC-tagged news items
 *
 * All data is live; nothing is manufactured. When a feed is unavailable the
 * panel degrades gracefully (banner shows "status unverified", counts show —).
 * House style mirrors ExecutiveBriefing / the former CoastalBrief.
 */

import { useMemo } from "react";
import type {
  ConflictIncident,
  ConflictEventType,
  SecurityStatus,
  IntelligenceItem,
  FallbackTier,
} from "@mtt/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  incidents: ConflictIncident[];
  status: SecurityStatus | null;
  news: IntelligenceItem[];
  loading: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

const WINDOW_DAYS = 30;

const EVENT_LABEL: Record<ConflictEventType, string> = {
  "bombing-ied": "Bombing / IED",
  shooting: "Shooting",
  "armed-clash": "Armed clash",
  arson: "Arson",
  "raid-arrest": "Raid / arrest",
  abduction: "Abduction",
  "remote-violence": "Remote violence",
  other: "Other",
};

export function SecurityBrief({ incidents, status, news, loading, ageMinutes, fallbackTier }: Props) {
  const summary = useMemo(() => {
    const cutoff = Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const recent = incidents.filter((i) => {
      const t = new Date(i.date).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    });
    const fatalities = recent.reduce((sum, i) => sum + (Number.isFinite(i.fatalities) ? i.fatalities : 0), 0);
    const byType = new Map<ConflictEventType, number>();
    for (const i of recent) byType.set(i.eventType, (byType.get(i.eventType) ?? 0) + 1);
    const topTypes = [...byType.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
    return { count: recent.length, fatalities, topTypes };
  }, [incidents]);

  const secNews = useMemo(
    () => news.filter((n) => n.tags.includes("SEC")).slice(0, 3),
    [news],
  );

  if (loading && incidents.length === 0 && !status) {
    return (
      <div className="col">
        <PanelHeader title="SECURITY BRIEF // DEEP SOUTH" source="acled" />
        <div className="skeleton" style={{ height: 32, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, width: "70%", marginTop: 8 }} />
      </div>
    );
  }

  const martial = status?.martialLaw ?? false;
  const emergency = status?.emergencyDecree ?? false;
  const bannerActive = martial || emergency;
  const bannerLabel = status?.label
    ?? (bannerActive ? "Special security law in force" : "Legal status unverified");

  return (
    <div className="col security-brief">
      <PanelHeader
        title="SECURITY BRIEF // DEEP SOUTH"
        source="acled / deep south watch"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
      />

      {/* Martial-law / emergency-decree status banner */}
      <div
        className="security-status-banner"
        style={{
          borderLeft: `1px solid ${bannerActive ? "var(--bad)" : "var(--line)"}`,
          padding: "6px 10px",
          marginTop: 8,
          background: bannerActive ? "color-mix(in srgb, var(--bad) 12%, transparent)" : "transparent",
        }}
      >
        <div className="eyebrow mono" style={{ color: bannerActive ? "var(--bad)" : "var(--text-3)" }}>
          {bannerActive ? "▲ SPECIAL SECURITY LAW" : "◆ LEGAL STATUS"}
        </div>
        <div className="mono" style={{ fontSize: "0.72rem", marginTop: 2 }}>{bannerLabel}</div>
        <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 2 }}>
          {martial ? "MARTIAL LAW" : "—"} · {emergency ? "EMERGENCY DECREE" : "—"}
          {status?.verifiedAt ? ` · verified ${status.verifiedAt}` : ""}
        </div>
      </div>

      {/* 30-day incident + fatality counters */}
      <div className="security-counters" style={{ display: "flex", gap: 12, marginTop: 10 }}>
        <div>
          <div className="eyebrow">INCIDENTS · {WINDOW_DAYS}D</div>
          <div className="mono" style={{ fontSize: "1.4rem", color: "var(--bad)" }}>
            {summary.count}
          </div>
        </div>
        <div>
          <div className="eyebrow">FATALITIES · {WINDOW_DAYS}D</div>
          <div className="mono" style={{ fontSize: "1.4rem", color: "var(--bad)" }}>
            {summary.fatalities}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="eyebrow">TOP EVENT TYPES</div>
          {summary.topTypes.length === 0 ? (
            <div className="mono eyebrow" style={{ color: "var(--text-3)" }}>no recent events</div>
          ) : (
            summary.topTypes.map(([type, n]) => (
              <div key={type} className="mono" style={{ fontSize: "0.66rem" }}>
                {EVENT_LABEL[type]} · {n}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Top SEC-tagged news */}
      {secNews.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="eyebrow">SECURITY HEADLINES</div>
          {secNews.map((item) => (
            <a
              key={item.id}
              href={item.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="security-news-row"
              style={{ display: "block", marginTop: 4, textDecoration: "none", color: "inherit" }}
            >
              <div className="mono" style={{ fontSize: "0.68rem", lineHeight: 1.25 }}>{item.title}</div>
              <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
                {item.source}{item.placeName ? ` · ${item.placeName}` : ""}
              </div>
            </a>
          ))}
        </div>
      )}

      <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 8 }}>
        YALA · PATTANI · NARATHIWAT — geocoded conflict events, last {WINDOW_DAYS} days
      </div>
    </div>
  );
}
